import { DATEPICKER_VARIANTS } from "@miplatform/constants";
import { DatePicker } from "./components/DatePicker";
import { HighlightedDatePicker } from "./HighlightedDatePicker";
import { MiDatePickerInline } from "./MiDatePickerInline";

/**
 * MiDatePicker - A wrapper component for the Material-UI `DesktopDatePicker` that allows for different date picker variants.
 *
 * This component supports multiple variants of date pickers, such as `Desktop` and `Inline`, which can be configured via the `variant` prop.
 * It will conditionally render the appropriate date picker based on the `variant` prop value, with the `Inline` variant using a custom `MiDatePickerInline` component.
 * The `Desktop` variant uses the default MUI `DesktopDatePicker`.
 *
 *  *
 * @param {object} props - The props passed to customize the date picker.
 * @param {boolean|string} [props.error=false] - If `true`, the input will be displayed in an error state. If a string is provided, it will be shown as helper text.
 * @param {function} [props.onChange] - Callback function that is called when the date is changed. Receives the new date as a parameter.
 * @param {moment.MomentInput|null} props.value - The currently selected date. Can be a `moment.Moment` object or null.
 * @param {boolean} [props.isFullWidth] - If true, the input takes full width.
 * @param {string} [props.variant=DATEPICKER_VARIANTS.DESKTOP] - The variant of the date picker to render.
 * @param {string} [props.label] - The label for the date picker.
 * @param {boolean} [props.disabled] - If true, the date picker is disabled and cannot be interacted with.
 *    Can be `DATEPICKER_VARIANTS.DESKTOP` (default) or `DATEPICKER_VARIANTS.INLINE` to display the inline date picker.
 * @returns {JSX.Element} The rendered date picker component based on the selected variant.
 */

export function MiDatePicker(props) {
  const { variant = DATEPICKER_VARIANTS.DESKTOP } = props;

  switch (variant) {
    case DATEPICKER_VARIANTS.INLINE:
      return <MiDatePickerInline {...props} />;

    case DATEPICKER_VARIANTS.HIGHLIGHT:
      return <HighlightedDatePicker {...props} />;

    default:
      return <DatePicker {...props} />;
  }
}
